import Head from 'next/head';
import Link from 'next/link';
import Header from '../../components/Header';

export default function LeakyPlumbingPage() {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const serviceName = "Leaky Pipe, Sink & Toilet Repair";
  const serviceArea = "Kane County, Geneva, Saint Charles, South Elgin";

  const leakServices = [
    {
      title: "Leaky Pipe Repair",
      description: "Professional repair for leaking pipes, joints, and fittings throughout your home",
      features: ["Joint seal repair", "Pipe patching", "Fitting replacement", "Preventive maintenance"]
    },
    {
      title: "Sink Leak Repair",
      description: "Complete sink leak repair including faucets, drains, and under-sink plumbing",
      features: ["Faucet leak repair", "Drain seal replacement", "Supply line fixes", "Disposal connections"]
    },
    {
      title: "Toilet Leak Repair",
      description: "Expert toilet leak repair for base leaks, tank leaks, and running toilets",
      features: ["Wax ring replacement", "Tank component repair", "Flapper adjustments", "Fill valve replacement"]
    },
    {
      title: "Emergency Leak Service",
      description: "Emergency service for major leaks that require immediate attention",
      features: ["Water shut-off assistance", "Damage prevention", "Same-day repairs", "Insurance documentation"]
    }
  ];

  const leakSigns = [
    "Water pooling around fixtures",
    "Unexplained water bills increases",
    "Musty odors or mold growth",
    "Discolored walls or ceilings",
    "Running water sounds when not in use",
    "Reduced water pressure"
  ];

  const faqItems = [
    {
      question: "How do I know if I have a hidden leak?",
      answer: "Signs include unexplained increases in water bills, the sound of running water when no fixtures are in use, wet spots on walls or floors, and musty odors. We can perform leak detection tests to locate hidden leaks."
    },
    {
      question: "Can small leaks cause major damage?",
      answer: "Yes, even small leaks can cause significant damage over time, including mold growth, structural damage, and expensive repairs. It's best to address leaks immediately to prevent costly problems."
    },
    {
      question: "How much does leak repair typically cost?",
      answer: "Repair costs vary depending on the location and severity of the leak. Simple faucet repairs may start around $75, while pipe repairs can range from $150-$400. We provide upfront pricing before any work begins."
    },
    {
      question: "Do you offer emergency leak repair services?",
      answer: "Yes, we provide emergency leak repair services throughout Kane County. For major leaks that could cause water damage, we respond quickly to minimize damage and begin repairs."
    }
  ];

  return (
    <>
      <Head>
        <title>{`Toilet Repair Near Me | Sink Leak Repair Geneva, IL | ${companyName}`}</title>
        <meta name="description" content={`Toilet repair near me in Geneva, St Charles & Kane County, IL. Leaky pipe repair, sink leak repair, toilet leak repair. Fast plumbing leak repair. Call ${phoneNumber} for same-day service!`} />
        <meta name="keywords" content="toilet repair near me, leaky pipe repair, sink leak repair, toilet leak repair Geneva IL, pipe leak repair near me, faucet repair Kane County, drain leak repair" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/services/leaky-plumbing" />
        <link rel="icon" href="/favicon.ico" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": companyName,
            "description": `Professional leak repair services in ${serviceArea}`,
            "telephone": phoneNumber,
            "serviceArea": serviceArea,
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Illinois",
              "addressLocality": "Kane County",
              "postalCode": "60177"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Leak Repair Services",
              "itemListElement": leakServices.map(service => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.description
                }
              }))
            }
          })}
        </script>
      </Head>

      <div className="min-h-screen bg-white">
        <Header currentPage="/services/leaky-plumbing" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Sticky Contact Button */}
        <div className="fixed bottom-6 right-6 z-50">
          <Link 
            href="/contact" 
            className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors font-bold text-lg animate-pulse"
          >
            Stop Leaks Now
          </Link>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Leaky Pipe, Sink & Toilet Repair
              </h1>
              <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-3xl mx-auto">
                Professional leak repair services in {serviceArea}. Stop leaks before they cause costly damage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a 
                  href={`tel:${phoneNumber}`} 
                  className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
                >
                  Call Now: {phoneNumber}
                </a>
                <Link 
                  href="/contact" 
                  className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
                >
                  Get Free Quote Online
                </Link>
              </div>
              <p className="text-slate-200 text-sm">
                🔧 Same-day service available | 💰 Free leak detection | 🛡️ Licensed & insured
              </p>
            </div>
          </div>
        </section>

        {/* Leak Warning Signs */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Signs You Have a Leak</h2>
              <p className="text-xl text-gray-600">Don't ignore these warning signs of plumbing leaks</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {leakSigns.map((sign, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex-shrink-0 mr-3"></div>
                    <p className="text-gray-900 font-medium">{sign}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg text-gray-700 mb-4">Early detection saves money and prevents damage! For severe leaks requiring immediate attention, contact our <Link href="/services/emergency-plumbing-service" className="text-blue-600 hover:text-blue-800 font-semibold underline">emergency plumber in Kane County</Link>.</p>
              <a href={`tel:${phoneNumber}`} className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors">
                Schedule Leak Detection: {phoneNumber}
              </a>
            </div>
          </div>
        </section>

        {/* Leak Repair Services */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Leak Repair Services</h2>
              <p className="text-xl text-gray-600">Comprehensive leak detection and repair solutions</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {leakServices.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link href="/contact" className="bg-yellow-500 text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors inline-block">
                    Get Free Estimate
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid-Page Contact Section */}
        <section className="py-16 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stop Small Leaks Before They Become Big Problems!</h2>
            <p className="text-xl text-green-100 mb-8">
              Even small leaks can cause thousands in damage. Our expert plumbers quickly locate and fix leaks to protect your property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                🔧 Call: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                📋 Schedule Repair Online
              </Link>
            </div>
            <p className="text-green-200 text-sm">
              ✅ Fast Response Times | ✅ Professional Equipment | ✅ Guaranteed Repairs
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Get answers to common questions about leak repair</p>
            </div>
            
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stop Leaks Before They Cause Damage
            </h2>
            <p className="text-xl text-slate-100 mb-8 max-w-3xl mx-auto">
              Don't let small leaks become big problems. Our experienced plumbers provide fast, reliable leak repair services throughout {serviceArea}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Call Now: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Service Online
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services in {serviceArea}</p>
                <p className="text-gray-300">Licensed & Insured | Emergency Service Available</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Service</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>Phone: <a href={`tel:${phoneNumber}`} className="hover:text-white">{phoneNumber}</a></li>
                  <li>Email: <a href="mailto:info@partnersinplumbing.com" className="hover:text-white">info@partnersinplumbing.com</a></li>
                  <li><Link href="/contact" className="hover:text-white">Contact Form</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
} 